import { Box, CircularProgress, Grid2 as Grid, Paper, Tab, Tabs, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai'
import * as React from 'react'

import { useGroupSession } from '../contexts/GroupSessionContext'
import { usePrivateSession } from '../contexts/PrivateSessionContext'
import { SESSION_TYPE, STATUS } from '../share/Constant'
import GroupSessionCard from './cards/GroupSessionCard'
import PrivateSessionCard from './cards/PrivateSessionCard'
import { currentUserAtom } from '../domains/user/AtomUsers'

export default function StudentBookingHistory() {
  const user = useAtomValue(currentUserAtom)
  const [tabValue, setTabValue] = React.useState(0)
  const [pageInited, setPageInited] = React.useState(false)
  const { upcomingGroupSessions, pastGroupSessions, loading: loadingGroupSessions, fetchUpComingGroupSessions, fetchPastGroupSessions } = useGroupSession()
  const { myPrivateSessions, fetchMyPrivateSessions, isLoading } = usePrivateSession()

  React.useEffect(() => {
    fetchMyPrivateSessions()
    fetchUpComingGroupSessions()
    fetchPastGroupSessions()
  }, [fetchMyPrivateSessions, fetchUpComingGroupSessions, fetchPastGroupSessions])

  const onChange = () => {
    fetchMyPrivateSessions()
    fetchUpComingGroupSessions()
    fetchPastGroupSessions()
  }

  const allUpcomingSessions = React.useMemo(() => {
    const upcoming = [
      ...upcomingGroupSessions.filter((session) => session?.participantIds?.includes(user?.resourceId))?.map((session) => ({ ...session, type: SESSION_TYPE.GROUP })),
      ...myPrivateSessions
        .filter((session) => dayjs(session.endTime).isAfter(dayjs(), 'minute') && session.status !== STATUS.COMPLETED)
        .map((session) => ({ ...session, type: SESSION_TYPE.PRIVATE })),
    ]
    return upcoming.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
  }, [upcomingGroupSessions, myPrivateSessions, user])

  const allPastSessions = React.useMemo(() => {
    const past = [
      ...pastGroupSessions.filter((session) => session?.participantIds?.includes(user?.resourceId))?.map((session) => ({ ...session, type: SESSION_TYPE.GROUP })),
      ...myPrivateSessions
        .filter((session) => dayjs(session.endTime).isBefore(dayjs(), 'minute') || session.status === STATUS.COMPLETED)
        .map((session) => ({ ...session, type: SESSION_TYPE.PRIVATE })),
    ]

    return past.filter((session) => session.status !== STATUS.AVAILABLE).sort((a, b) => -dayjs(a.date).diff(dayjs(b.date)))
  }, [pastGroupSessions, myPrivateSessions, user])

  const renderSessionList = (sessions, noSessionsText) => {
    if (sessions.length === 0) {
      return (
        <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
          {noSessionsText}
        </Typography>
      )
    }

    return sessions.map((session) => {
      if (session.type === SESSION_TYPE.GROUP) {
        return <GroupSessionCard key={session.resourceId} session={session} mode="small" updateHook={onChange} deleteHook={onChange} user={user} />
      } else {
        return <PrivateSessionCard key={session.resourceId} session={session} updateHook={onChange} deleteHook={onChange} />
      }
    })
  }

  React.useEffect(() => {
    if (!pageInited && (isLoading || loadingGroupSessions)) {
      setPageInited(true)
    }
  }, [isLoading, loadingGroupSessions, pageInited])

  if (!pageInited) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    )
  }
  return (
    <Box sx={{ p: 2 }}>
      {/* 页面标题 */}
      <Typography variant="h5" gutterBottom>
        My Booked Sessions
      </Typography>

      {/* 课程列表区域 */}
      <Paper sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {/* 标签页切换 */}
        <Tabs
          value={tabValue}
          onChange={(event, newValue) => {
            setTabValue(newValue)
          }}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab label="Upcoming" />
          <Tab label="History" />
        </Tabs>

        <Grid container spacing={1}>
          {tabValue === 0 ? <>{renderSessionList(allUpcomingSessions, 'No upcoming sessions')}</> : <>{renderSessionList(allPastSessions, 'No past sessions')}</>}
        </Grid>
      </Paper>
    </Box>
  )
}
